import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  clearCookieConsent,
  readConsentStatus,
  writeCookieConsent,
  type ConsentStatus,
} from './cookie-consent';

export interface CookieConsentContextValue {
  status: ConsentStatus;
  /** Expliciete keuze gedaan (noodzakelijk of analytics). */
  hasAnswered: boolean;
  analyticsEnabled: boolean;
  acceptNecessaryOnly: () => void;
  acceptAnalytics: () => void;
  /** Verwijdert keuze; toont banner opnieuw (footer “Cookievoorkeuren”). */
  reopenBanner: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

function statusToState(): ConsentStatus {
  if (typeof window === 'undefined') return 'unknown';
  return readConsentStatus();
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<ConsentStatus>(statusToState);

  const acceptNecessaryOnly = useCallback(() => {
    writeCookieConsent(false);
    setStatus('necessary_only');
  }, []);

  const acceptAnalytics = useCallback(() => {
    writeCookieConsent(true);
    setStatus('analytics');
  }, []);

  const reopenBanner = useCallback(() => {
    clearCookieConsent();
    setStatus('unknown');
  }, []);

  const value = useMemo<CookieConsentContextValue>(
    () => ({
      status,
      hasAnswered: status !== 'unknown',
      analyticsEnabled: status === 'analytics',
      acceptNecessaryOnly,
      acceptAnalytics,
      reopenBanner,
    }),
    [status, acceptNecessaryOnly, acceptAnalytics, reopenBanner]
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent(): CookieConsentContextValue {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return ctx;
}
